/**
 * UniversalSectionRenderer.js
 *
 * A single renderer that handles all 14 resume sections for any template.
 * Templates declare only their layout, typography, and style tokens.
 * This renderer handles the actual section rendering logic.
 *
 * Sections supported:
 *   summary, experience, education, projects, skills,
 *   certifications, achievements, awards, languages,
 *   publications, volunteer, interests, references, custom
 *
 * A section renders ONLY if:
 *   1. It appears in the template's sectionOrder
 *   2. It has actual data (non-empty arrays, non-empty strings)
 *
 * Usage:
 *   <UniversalSectionRenderer
 *     resumeData={resumeData}
 *     templateConfig={templateConfig}
 *     accentColor="#1a1a2e"
 *     fontScale={1}
 *     editMode={false}
 *     onFieldUpdate={fn}
 *     onDeleteBullet={fn}
 *     onDeleteSkill={fn}
 *   />
 */
import React, { memo } from 'react';
import EditableField from '../components/EditableField';
import {
  SectionHeader, BulletList, SkillsBlock, DS,
} from './templateDesignSystem';
import { normalizeTemplateData } from './templateHelpers';

// ─── Canonical section order (all 14 sections) ─────────────────────────────
export const ALL_SECTIONS = [
  'personal-info',
  'summary',
  'experience',
  'education',
  'projects',
  'skills',
  'certifications',
  'achievements',
  'awards',
  'languages',
  'publications',
  'volunteer',
  'interests',
  'references',
  'custom',
];

// ─── Normalize points from any known key alias ──────────────────────────────
export function normalizePoints(item) {
  if (!item) return [];
  const raw = item.points ?? item.bullets ?? item.responsibilities ?? item.description ?? [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) return [raw];
  return [];
}

// ─── Section guard: returns true if there's actual data ────────────────────
function hasData(value) {
  if (!value) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return false;
}

// ─── Memoized section wrappers ─────────────────────────────────────────────
// Each section wrapped in React.memo so it only re-renders when its slice changes

const SummarySection = memo(function SummarySection({ data, accentColor, fontScale, editMode, onFieldUpdate }) {
  const { summaryText } = data;
  // Always skip rendering if there is no summary text — even in editMode.
  // Rendering an empty section creates visible blank space in all templates.
  if (!hasData(summaryText)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="summary" key="summary" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Professional Summary" accentColor={accentColor} />
      <EditableField
        id="summary"
        editMode={editMode}
        value={summaryText || ''}
        onSave={v => onFieldUpdate('summary', v)}
        tag="p"
        multiline
        style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0, textAlign: 'justify' }}
        placeholder="Write a professional summary..."
      />
    </section>
  );
});

const ExperienceSection = memo(function ExperienceSection({ data, accentColor, fontScale, editMode, onFieldUpdate, onDeleteBullet }) {
  const { experience } = data;
  if (!hasData(experience)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="experience" key="experience" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Work Experience" accentColor={accentColor} />
      {experience.map((exp, idx) => {
        const points = normalizePoints(exp);
        return (
          <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <EditableField
                  id={`experience.${idx}.role`}
                  editMode={editMode}
                  value={exp.role || exp.title || ''}
                  onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                  tag="h3"
                  style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }}
                />
                {exp.company && (
                  <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{exp.company}</span>
                )}
              </div>
              <EditableField
                id={`experience.${idx}.duration`}
                editMode={editMode}
                value={exp.duration || exp.date || ''}
                onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                tag="span"
                style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8, fontStyle: 'italic' }}
              />
            </div>
            {exp.location && (
              <div style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', marginTop: 1 }}>{exp.location}</div>
            )}
            <BulletList points={points} accentColor={accentColor} />
          </div>
        );
      })}
    </section>
  );
});

const EducationSection = memo(function EducationSection({ data, accentColor, fontScale, editMode, onFieldUpdate }) {
  const { educationList } = data;
  if (!hasData(educationList)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="education" key="education" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Education" accentColor={accentColor} />
      {educationList.map((edu, idx) => {
        const collegeName = edu?.college || edu?.school || edu?.institution || edu?.university || '';
        return (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <EditableField
                id={`education.${idx}.degree`}
                editMode={editMode}
                value={edu?.degree || ''}
                onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                tag="h3"
                style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }}
              />
              {(collegeName || editMode) && (
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>
                  {' — '}
                  <EditableField
                    id={`education.${idx}.college`}
                    editMode={editMode}
                    value={collegeName}
                    onSave={v => onFieldUpdate(`education.${idx}.college`, v)}
                    tag="span"
                    style={{ display: 'inline' }}
                    placeholder="College/School"
                  />
                </span>
              )}
              {edu?.cgpa && (
                <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280' }}>{' · CGPA: '}{edu.cgpa}</span>
              )}
            </div>
            <EditableField
              id={`education.${idx}.year`}
              editMode={editMode}
              value={edu?.year || edu?.graduation || ''}
              onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
              tag="span"
              style={{ fontSize: fs(DS.fs.small), color: '#6B7280', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 8 }}
            />
          </div>
        );
      })}
    </section>
  );
});

const ProjectsSection = memo(function ProjectsSection({ data, accentColor, fontScale, editMode, onFieldUpdate }) {
  const { projects } = data;
  if (!hasData(projects)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="projects" key="projects" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Projects" accentColor={accentColor} />
      {projects.map((proj, idx) => {
        // Resolve description
        const descriptionText = typeof proj.description === 'string' ? proj.description : '';
        
        // Resolve points: if description is a string and was the only source, don't duplicate it as a bullet.
        let points = [];
        const rawPoints = proj.points ?? proj.bullets ?? proj.responsibilities ?? [];
        if (Array.isArray(rawPoints)) {
          points = rawPoints.filter(Boolean);
        } else if (typeof rawPoints === 'string' && rawPoints.trim()) {
          points = [rawPoints];
        } else if (!descriptionText && typeof proj.description !== 'string') {
          // If no rawPoints and description is an array, use it
          points = normalizePoints(proj);
        }

        return (
          <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <EditableField
                  id={`projects.${idx}.title`}
                  editMode={editMode}
                  value={proj.title || proj.name || 'Project'}
                  onSave={v => onFieldUpdate(`projects.${idx}.title`, v)}
                  tag="span"
                  style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111' }}
                />
                {proj.tech?.length > 0 && (
                  <span style={{ fontSize: fs(DS.fs.small), color: accentColor, marginLeft: 6, fontStyle: 'italic' }}>
                    [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                  </span>
                )}
              </div>
              {(proj.impact || proj.date || editMode) && (
                <EditableField
                  id={`projects.${idx}.date`}
                  editMode={editMode}
                  value={proj.impact || proj.date || ''}
                  onSave={v => onFieldUpdate(`projects.${idx}.date`, v)}
                  tag="span"
                  style={{ fontSize: fs(DS.fs.small), color: '#6B7280', fontStyle: 'italic' }}
                  placeholder="Date/Impact"
                />
              )}
            </div>
            {/* Render description paragraph if it exists or if we are editing to allow entering one */}
            {(descriptionText || editMode) && (
              <EditableField
                id={`projects.${idx}.description`}
                editMode={editMode}
                value={descriptionText}
                onSave={v => onFieldUpdate(`projects.${idx}.description`, v)}
                tag="p"
                multiline
                style={{ fontSize: fs(DS.fs.body), color: '#4B5563', margin: '3px 0 0', lineHeight: 1.5, textAlign: 'justify' }}
                placeholder="Write a project description..."
              />
            )}
            {/* Only render bullets if they exist — no blank placeholder */}
            {points.length > 0 && <BulletList points={points} accentColor={accentColor} />}
          </div>
        );
      })}
    </section>
  );
});

const SkillsSection = memo(function SkillsSection({ data, accentColor, fontScale, onDeleteSkill }) {
  const { skills, skillCategories } = data;
  if (!hasData(skills)) return null;
  return (
    <section data-ats="skills" key="skills" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Skills" accentColor={accentColor} />
      <SkillsBlock
        skills={skills}
        skillCategories={skillCategories}
        accentColor={accentColor}
        layout="rows"
        onDeleteSkill={onDeleteSkill}
      />
    </section>
  );
});

const CertificationsSection = memo(function CertificationsSection({ data, accentColor, fontScale }) {
  const { certList } = data;
  if (!hasData(certList)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="certifications" key="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Certifications" accentColor={accentColor} />
      {certList.map((cert, idx) => {
        const text = typeof cert === 'string' ? cert : (cert.name || cert.title || JSON.stringify(cert));
        const issuer = cert.issuer || cert.organization || null;
        const year = cert.year || cert.date || null;
        return (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: fs(DS.fs.body), color: '#374151' }}>
              {text}
              {issuer && <span style={{ color: '#6B7280' }}>{' — '}{issuer}</span>}
            </span>
            {year && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{year}</span>}
          </div>
        );
      })}
    </section>
  );
});

const AchievementsSection = memo(function AchievementsSection({ data, accentColor }) {
  const { achieveList } = data;
  if (!hasData(achieveList)) return null;
  return (
    <section data-ats="achievements" key="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Achievements" accentColor={accentColor} />
      <BulletList points={achieveList} accentColor={accentColor} />
    </section>
  );
});

const AwardsSection = memo(function AwardsSection({ data, accentColor, fontScale }) {
  // awards can live under data.awards or data.honors
  const awards = data.awards || data.honors || [];
  if (!hasData(awards)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="awards" key="awards" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Awards & Honors" accentColor={accentColor} />
      {awards.map((award, idx) => {
        const text = typeof award === 'string' ? award : (award.title || award.name || JSON.stringify(award));
        const org = award.organization || award.issuer || null;
        const yr = award.year || award.date || null;
        return (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: fs(DS.fs.body), color: '#374151' }}>
              {text}
              {org && <span style={{ color: '#6B7280' }}>{' — '}{org}</span>}
            </span>
            {yr && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8 }}>{yr}</span>}
          </div>
        );
      })}
    </section>
  );
});

const LanguagesSection = memo(function LanguagesSection({ data, accentColor, fontScale }) {
  const { languageList } = data;
  if (!hasData(languageList)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="languages" key="languages" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Languages" accentColor={accentColor} />
      <p style={{ fontSize: fs(DS.fs.body), color: '#374151', margin: 0, lineHeight: 1.6 }}>
        {languageList.map(l => (typeof l === 'string' ? l : `${l.name}${l.level ? ` (${l.level})` : ''}`)).join('   ·   ')}
      </p>
    </section>
  );
});

const PublicationsSection = memo(function PublicationsSection({ data, accentColor, fontScale }) {
  const publications = data.publications || [];
  if (!hasData(publications)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="publications" key="publications" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Publications" accentColor={accentColor} />
      {publications.map((pub, idx) => {
        const title = typeof pub === 'string' ? pub : (pub.title || pub.name || JSON.stringify(pub));
        const journal = pub.journal || pub.venue || pub.publisher || null;
        const yr = pub.year || pub.date || null;
        return (
          <div key={idx} style={{ marginBottom: 6 }}>
            <span style={{ fontSize: fs(DS.fs.body), fontWeight: 600, color: '#111' }}>{title}</span>
            {journal && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', display: 'block', marginTop: 1 }}>{journal}{yr ? `, ${yr}` : ''}</span>}
          </div>
        );
      })}
    </section>
  );
});

const VolunteerSection = memo(function VolunteerSection({ data, accentColor, fontScale }) {
  const volunteer = data.volunteer || data.volunteering || [];
  if (!hasData(volunteer)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="volunteer" key="volunteer" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Volunteer Experience" accentColor={accentColor} />
      {volunteer.map((v, idx) => {
        const points = normalizePoints(v);
        const title = typeof v === 'string' ? v : (v.role || v.title || v.name || JSON.stringify(v));
        const org = v.organization || v.company || null;
        const dur = v.duration || v.date || null;
        return (
          <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111' }}>{title}</span>
                {org && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{org}</span>}
              </div>
              {dur && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 8, fontStyle: 'italic' }}>{dur}</span>}
            </div>
            {points.length > 0 && <BulletList points={points} accentColor={accentColor} />}
          </div>
        );
      })}
    </section>
  );
});

const InterestsSection = memo(function InterestsSection({ data, accentColor, fontScale }) {
  const { interests, hobbies } = data;
  const items = hasData(interests) ? interests : (hasData(hobbies) ? hobbies : []);
  if (!hasData(items)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="interests" key="interests" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Interests" accentColor={accentColor} />
      <p style={{ fontSize: fs(DS.fs.body), color: '#374151', margin: 0, lineHeight: 1.6 }}>
        {Array.isArray(items) ? items.join('  ·  ') : items}
      </p>
    </section>
  );
});

const ReferencesSection = memo(function ReferencesSection({ data, accentColor, fontScale }) {
  const references = data.references || [];
  if (!hasData(references)) return null;
  const fs = (base) => `${parseFloat(base) * fontScale}px`;
  return (
    <section data-ats="references" key="references" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="References" accentColor={accentColor} />
      {references.length === 0 || (references.length === 1 && references[0]?.toLowerCase?.() === 'available on request') ? (
        <p style={{ fontSize: fs(DS.fs.body), color: '#6B7280', margin: 0, fontStyle: 'italic' }}>
          Available upon request
        </p>
      ) : (
        references.map((ref, idx) => {
          const name = typeof ref === 'string' ? ref : (ref.name || JSON.stringify(ref));
          const title = ref.title || ref.role || null;
          const contact = ref.email || ref.phone || null;
          return (
            <div key={idx} style={{ marginBottom: 6 }}>
              <span style={{ fontSize: fs(DS.fs.body), fontWeight: 600, color: '#111' }}>{name}</span>
              {title && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', display: 'block' }}>{title}</span>}
              {contact && <span style={{ fontSize: fs(DS.fs.small), color: '#6B7280', display: 'block' }}>{contact}</span>}
            </div>
          );
        })
      )}
    </section>
  );
});

const ExtracurricularSection = memo(function ExtracurricularSection({ data, accentColor }) {
  const { extraList } = data;
  if (!hasData(extraList)) return null;
  return (
    <section data-ats="extracurricular" key="extracurricular" style={{ marginBottom: DS.sp.sectionMb }}>
      <SectionHeader title="Extracurricular Activities" accentColor={accentColor} />
      <BulletList points={extraList} accentColor={accentColor} />
    </section>
  );
});

// ─── Section key → component map ───────────────────────────────────────────
const SECTION_COMPONENTS = {
  summary: SummarySection,
  experience: ExperienceSection,
  education: EducationSection,
  projects: ProjectsSection,
  skills: SkillsSection,
  certifications: CertificationsSection,
  achievements: AchievementsSection,
  awards: AwardsSection,
  languages: LanguagesSection,
  publications: PublicationsSection,
  volunteer: VolunteerSection,
  interests: InterestsSection,
  references: ReferencesSection,
  extracurricular: ExtracurricularSection,
  // Aliases
  internships: ExperienceSection,
};

// ─── Main Renderer ─────────────────────────────────────────────────────────
export default function UniversalSectionRenderer({
  resumeData,
  templateConfig,
  accentColor = '#1a1a2e',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => {},
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return null;

  const data = normalizeTemplateData(resumeData);

  // Determine section order: user custom order → template config → fallback to ALL_SECTIONS
  const userOrder = Array.isArray(resumeData?.sections)
    ? resumeData.sections.map(k => k === 'contact' ? 'personal-info' : k)
    : null;
  const templateOrder = userOrder && userOrder.length > 0
    ? userOrder
    : (templateConfig?.sectionOrder || ALL_SECTIONS);

  // Merge: render all sections in template order, then any in data not declared
  const renderedKeys = new Set();
  const orderedKeys = [
    ...templateOrder,
    ...ALL_SECTIONS.filter(k => !templateOrder.includes(k)),
  ].filter(k => {
    if (k === 'personal-info') return false; // handled by header
    if (k === 'custom') return false; // skip for now
    if (renderedKeys.has(k)) return false;
    renderedKeys.add(k);
    return true;
  });

  return (
    <>
      {orderedKeys.map(key => {
        const Component = SECTION_COMPONENTS[key];
        if (!Component) return null;
        return (
          <Component
            key={key}
            data={data}
            accentColor={accentColor}
            fontScale={fontScale}
            editMode={editMode}
            onFieldUpdate={onFieldUpdate}
            onDeleteBullet={onDeleteBullet}
            onDeleteSkill={onDeleteSkill}
          />
        );
      })}
    </>
  );
}
